import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Typography } from 'src/modules/core/components'
import { describeSaveFailure, formatBrandingMessage } from './booking-branding-card.domain'
import type { BookingBrandingCardProps, BrandingMessage, BrandingSaveFailure } from './booking-branding-card.interface'
import classes from './booking-branding-card.module.scss'
import { BrandingAccentPicker } from './branding-accent-picker'
import { BrandingLogoPicker } from './branding-logo-picker'
import { BrandingNameField } from './branding-name-field'
import { BrandingPreview } from './branding-preview'
import { useBookingBrandingForm } from './use-booking-branding-form'
import { useSaveBookingBranding } from './use-save-booking-branding'

export function BookingBrandingCard({ bookingOnboarding, bookingPageUrl, accountName }: BookingBrandingCardProps) {
  const { formatMessage } = useIntl()
  const branding = useBookingBrandingForm(bookingOnboarding, accountName)
  const { saveError, isSaving, save } = useBrandingSave(branding)
  const { form, accent, name } = branding
  const logoSrc = form.logo?.kind === 'new' ? form.logo.objectUrl : (form.logo?.url ?? null)

  return (
    <section className={classes.card} aria-label={formatMessage({ id: 'settings.bookingBranding.title' })}>
      <div className={classes.titleRow}>
        <Typography variant="h5" className={classes.title}>
          {formatMessage({ id: 'settings.bookingBranding.title' })}
        </Typography>
        {branding.isDirty && (
          <span className={classes.unsavedPill}>
            {formatMessage({ id: 'settings.bookingBranding.unsavedChanges' })}
          </span>
        )}
      </div>
      <Typography variant="body" className={classes.subtitle}>
        {formatMessage({ id: 'settings.bookingBranding.subtitle' })}
      </Typography>
      <div className={classes.layout}>
        <div className={classes.controls}>
          <BrandingNameField
            value={form.name}
            placeholder={accountName}
            error={name.error}
            disabled={isSaving}
            onChange={name.change}
          />
          <BrandingLogoPicker
            logo={form.logo}
            error={branding.logoError}
            disabled={isSaving}
            onPick={branding.pickLogo}
            onRemove={branding.removeLogo}
          />
          <BrandingAccentPicker
            inputValue={form.accentInput}
            selectedSwatch={accent.selectedSwatch}
            pickerColor={accent.pickerColor}
            showError={accent.showError}
            disabled={isSaving}
            onSelect={accent.select}
            onChange={accent.change}
            onBlur={accent.blur}
          />
        </div>
        <BrandingPreview
          accent={branding.previewAccent}
          logoSrc={logoSrc}
          providerName={branding.previewName}
          bookingPageUrl={bookingPageUrl}
        />
      </div>
      <div className={classes.actions}>
        <button type="button" className={classes.resetButton} onClick={branding.resetToDefault} disabled={isSaving}>
          {formatMessage({ id: 'settings.bookingBranding.reset' })}
        </button>
        <div className={classes.actionButtons}>
          <Button
            variant="withBorder"
            widthType="content"
            onClick={branding.discard}
            disabled={!branding.isDirty || isSaving}
          >
            {formatMessage({ id: 'settings.bookingBranding.discard' })}
          </Button>
          <Button
            widthType="content"
            onClick={save}
            disabled={!branding.isDirty || !accent.isValid || !name.isValid || isSaving}
            isLoading={isSaving}
            loadingLabel={formatMessage({ id: 'settings.bookingBranding.saving' })}
          >
            {formatMessage({ id: 'settings.bookingBranding.save' })}
          </Button>
        </div>
      </div>
      {saveError && (
        <Typography variant="body" className={classes.saveError} role="alert">
          {formatBrandingMessage(saveError, formatMessage)}
        </Typography>
      )}
    </section>
  )
}

function useBrandingSave(branding: ReturnType<typeof useBookingBrandingForm>) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const saveBranding = useSaveBookingBranding()
  const [saveError, setSaveError] = useState<BrandingMessage | null>(null)
  const showFailure: Record<BrandingSaveFailure['target'], (message: BrandingMessage) => void> = {
    logo: branding.setLogoError,
    name: branding.name.setServerError,
    save: setSaveError,
  }

  const save = () => {
    const fileName = branding.form.logo?.kind === 'new' ? branding.form.logo.file.name : null
    // The inputs lock while saving and success resets the form, so a pick still decoding would be lost.
    branding.cancelLogoPick()
    setSaveError(null)
    branding.setLogoError(null)
    branding.name.setServerError(null)
    saveBranding
      .mutateAsync(branding.buildFormData())
      .then((dto) => {
        branding.applySavedDto(dto)
        enqueueSnackbar(formatMessage({ id: 'settings.bookingBranding.saved' }), { variant: 'success' })
      })
      .catch((error: unknown) => {
        const failure = describeSaveFailure(error, fileName)
        showFailure[failure.target](failure.message)
      })
  }

  return { saveError, isSaving: saveBranding.isLoading, save }
}
